import type { AiChatInput, AiChatReply, AiHintInput, AiHintReply, AiStatus, AiUsage, Permission } from "@cafe/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "@/shared/api/client";
import { useCan, useFeature } from "@/shared/hooks/session";

const STATUS_KEY = ["ai-status"];

/**
 * Помощник доступен пользователю (тариф + право) и подключён на сервере; `usage` — квота запросов за месяц.
 * `permission` — право раздела (подсказка склада — `stock.view`), по умолчанию — отчёты.
 */
export function useAiStatus(permission: Permission = "reports.view") {
  const can = useCan();
  const feature = useFeature();
  const allowed = feature("core.analytics_basic") && can(permission);
  const q = useQuery({ queryKey: STATUS_KEY, queryFn: () => http.get<AiStatus>("/ai/status"), enabled: allowed, staleTime: 5 * 60_000 });
  return { live: allowed && !!q.data?.enabled, loading: allowed && q.isLoading, usage: q.data?.usage };
}

/** Свежий расход квоты из ответа чата/подсказки — без лишнего запроса статуса. */
export function useSetAiUsage() {
  const qc = useQueryClient();
  return (usage: AiUsage | undefined) => {
    if (usage) qc.setQueryData<AiStatus>(STATUS_KEY, (s) => (s ? { ...s, usage } : s));
  };
}

export const aiApi = {
  chat: (b: AiChatInput) => http.post<AiChatReply>("/ai/chat", b),
  hint: (b: AiHintInput) => http.post<AiHintReply>("/ai/hint", b),
};
